import React, { useState, useEffect } from 'react';
import { 
  UserCheck, 
  Building2, 
  MapPin,
  Star,
  DollarSign,
  PlusCircle,
  Search,
  Filter,
  BarChart2,
  Settings,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { 
  AddProviderModal, 
  ProviderDetailsModal, 
  ProviderReviewModal 
} from '@/client/components/admin/Escrow/ServiceProviderModal/ServiceProviderModal';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

interface ServiceProvider {
  id: string;
  name: string;
  type: 'legal' | 'surveyor' | 'inspector';
  status: 'active' | 'pending' | 'suspended';
  rating: number;
  totalJobs: number;
  successRate: number;
  commission: number;
  location: string;
  lastActive: string;
  verificationStatus: 'verified' | 'pending' | 'rejected';
  email: string;
  phone: string;
  website: string;
  address: string;
  serviceAreas: string[];
  documents: {
    type: string;
    status: 'verified' | 'pending' | 'rejected';
  }[];
  recentActivity?: {
    description: string;
    date: string;
  }[];
  reviews?: {
    rating: number;
    user: string;
    date: string;
    comment: string;
  }[];
}

// Mock data for initial development
const MOCK_PROVIDERS: ServiceProvider[] = [
  {
    id: 'LC123456',
    name: 'Legal Corp Ltd',
    type: 'legal',
    status: 'active',
    rating: 4.8,
    totalJobs: 156,
    successRate: 95,
    commission: 2.5,
    location: 'New York, USA',
    lastActive: '2024-02-13',
    verificationStatus: 'verified',
    email: 'contact@legalcorp.com',
    phone: '+1 234 567 8900',
    website: 'www.legalcorp.com',
    address: '123 Legal Street, NY',
    serviceAreas: ['New York', 'New Jersey', 'Connecticut'],
    documents: [
      { type: 'Business Registration', status: 'verified' },
      { type: 'Professional License', status: 'verified' },
      { type: 'Insurance Certificate', status: 'pending' }
    ],
    recentActivity: [
      {
        description: 'Completed property verification',
        date: '2024-02-13'
      }
    ],
    reviews: [
      {
        rating: 5,
        user: 'John Doe',
        date: '2024-02-10',
        comment: 'Excellent service and very professional'
      }
    ]
  }
  // Add more mock providers as needed
];

const ServiceProviderManagement = () => {
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Service Provider Management' }
      ]);
  // State declarations
  const [activeTab, setActiveTab] = useState<'legal' | 'surveyor' | 'inspector'>('legal');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [showProviderDetails, setShowProviderDetails] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [providers, setProviders] = useState<ServiceProvider[]>(MOCK_PROVIDERS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Handlers
  const handleAddProvider = async (data: any) => {
    try {
      setIsLoading(true);
      setError(null);
      // In a real application, this would be an API call
      const newProvider = {
        ...data,
        id: `PRV${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString()
      };
      setProviders(prev => [...prev, newProvider]);
      setShowAddProviderModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add provider');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (provider: ServiceProvider) => {
    setSelectedProvider(provider);
    setShowProviderDetails(true);
  };

  const handleReviewSubmit = async (review: any) => {
    try {
      setIsLoading(true);
      setError(null);
      // In a real application, this would be an API call
      if (selectedProvider) {
        const updatedProvider = {
          ...selectedProvider,
          reviews: [...(selectedProvider.reviews || []), { ...review, date: new Date().toISOString() }]
        };
        setProviders(prev => 
          prev.map(p => p.id === selectedProvider.id ? updatedProvider : p)
        );
      }
      setShowReviewModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review');
    } finally {
      setIsLoading(false);
    }
  };

  // Filtering and pagination
  const filteredProviders = providers.filter(provider => {
    const matchesType = provider.type === activeTab;
    const matchesStatus = filterStatus === 'all' || provider.status === filterStatus;
    const matchesSearch = searchQuery === '' || 
      provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      provider.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProviders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);

  // Stats calculations
  const stats = {
    activeProviders: filteredProviders.filter(p => p.status === 'active').length,
    averageRating: filteredProviders.reduce((acc, p) => acc + p.rating, 0) / filteredProviders.length || 0,
    successRate: filteredProviders.reduce((acc, p) => acc + p.successRate, 0) / filteredProviders.length || 0,
    pendingVerifications: filteredProviders.filter(p => p.verificationStatus === 'pending').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Provider Type Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('legal')}
                className={`${
                  activeTab === 'legal'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Building2 className="h-5 w-5 mr-2" />
                Legal Companies
              </button>
              <button
                onClick={() => setActiveTab('surveyor')}
                className={`${
                  activeTab === 'surveyor'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <MapPin className="h-5 w-5 mr-2" />
                Surveyors
              </button>
              <button
                onClick={() => setActiveTab('inspector')}
                className={`${
                  activeTab === 'inspector'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Clock className="h-5 w-5 mr-2" />
                Vehicle Inspectors
              </button>
            </nav>
          </div>

          {/* Filters and Search */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search providers..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
                <div className="relative w-full sm:w-48">
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => setShowAddProviderModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                <PlusCircle className="h-5 w-5 mr-2" />
                Add New Provider
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Provider Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 border-b border-gray-200">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <UserCheck className="h-6 w-6 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Active Providers</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.activeProviders}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Star className="h-6 w-6 text-yellow-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Average Rating</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.averageRating.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <BarChart2 className="h-6 w-6 text-green-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Success Rate</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.successRate.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Clock className="h-6 w-6 text-purple-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pending Verifications</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {stats.pendingVerifications}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Providers Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="ml-2">Loading providers...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No providers found
                    </td>
                  </tr>
                ) : (
                  currentItems.map((provider) => (
                    <tr key={provider.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              {provider.type === 'legal' && <Building2 className="h-6 w-6 text-gray-500" />}
                              {provider.type === 'surveyor' && <MapPin className="h-6 w-6 text-gray-500" />}
                              {provider.type === 'inspector' && <MapPin className="h-6 w-6 text-gray-500" />}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {provider.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {provider.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{provider.location}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          provider.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : provider.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {provider.status.charAt(0).toUpperCase() + provider.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <span className="ml-1 text-sm text-gray-900">{provider.rating}</span>
                          <span className="ml-2 text-sm text-gray-500">
                            ({provider.successRate}% Success)
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {provider.commission}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(provider)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                      currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{indexOfFirstItem + 1}</span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredProviders.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{filteredProviders.length}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddProviderModal
        providerType={activeTab}
        isOpen={showAddProviderModal}
        onClose={() => setShowAddProviderModal(false)}
        onSubmit={handleAddProvider}
      />

      {selectedProvider && (
        <>
          <ProviderDetailsModal
            provider={selectedProvider}
            isOpen={showProviderDetails}
            onClose={() => {
              setShowProviderDetails(false);
              setSelectedProvider(null);
            }}
          />

          <ProviderReviewModal
            provider={selectedProvider}
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onSubmit={handleReviewSubmit}
          />
        </>
      )}
    </div>
  );
};

export default ServiceProviderManagement;